/**
 * @file Auth-sent route (`/auth/sent`) — "check your inbox" beat.
 * @module @labcraft/lab/ui/pages/auth-sent
 *
 * Shown after a successful signup or signin submission. Carries the
 * email + purpose in the query string so the resend link can re-issue
 * the right token type.
 */

import { useState, type JSX } from 'react'
import { useSearchParams } from 'react-router'
import { useSigninWithEmailMutation } from '../../hooks/use_signin_with_email.ts'
import styles from './auth.module.css'

export function AuthSentPage(): JSX.Element {
  const signinWithEmail = useSigninWithEmailMutation()
  const [search] = useSearchParams()
  const email = search.get('email') ?? ''
  const next = search.get('next') ?? undefined
  const purposeRaw = search.get('purpose')
  const purpose: 'signup' | 'signin' =
    purposeRaw === 'signup' ? 'signup' : 'signin'

  const [resent, setResent] = useState(false)

  async function onResend(): Promise<void> {
    if (!email || signinWithEmail.isPending) return
    try {
      await signinWithEmail.mutateAsync(next ? { email, next } : { email })
      setResent(true)
    } catch {
      // The form below stays available; the user can click again.
    }
  }

  const ttl = purpose === 'signup' ? '24 hours' : '15 minutes'

  return (
    <main className={styles.main}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>Check your inbox</p>
        <h1 className={styles.title}>One link, on its way.</h1>
        <p className={styles.lede}>
          It&apos;s headed to <strong>{email || 'your inbox'}</strong>. Open it
          to {purpose === 'signup' ? 'confirm your account' : 'sign in'} — the
          link works for the next {ttl}.
        </p>

        <p className={styles.fineprint}>
          Nothing yet?{' '}
          {resent ? (
            <span>Sent again — give it a minute to land.</span>
          ) : (
            <button
              type="button"
              className={styles.linkButton}
              onClick={onResend}
              disabled={signinWithEmail.isPending || !email}>
              {signinWithEmail.isPending ? 'Resending…' : 'Send a new one'}
            </button>
          )}
        </p>
      </section>
    </main>
  )
}
